import {
  AsyncMultiSelectField,
  AsyncSingleSelectField,
  CharField,
  ImageField,
  NumberField,
  RadiosField,
  SingleSelectField,
  SwitchField,
  TextAreaField,
} from "@inmagik/magik-ui"
import { getIn, useFormikContext } from "formik"
import { Col } from "react-bootstrap"
import { ImmagineFormType } from "../../types"
import { useEdizioneCreate, useEdizioneLazy, useEdizioniLazy } from "../../hooks/edizioni"
import EdizioneModal from "../Edizioni/EdizioneModal"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useTagCreate, useTagLazy, useTagsLazy } from "../../hooks/tags"
import TagModal from "../Tags/TagModal"
import {
  useTipologiaImmagineCreate,
  useTipologiaImmagineLazy,
  useTipologieImmaginiLazy,
} from "../../hooks/tipologie-immagini"
import TipologiaImmagineModal from "../TipologieImmagini/TipologiaImmagineModal"
import { usePersonaCreate, usePersonaLazy, usePersoneLazy } from "../../hooks/persone"
import { transformErrorForForm } from "../../utils/utils"
import { WysiwygField } from "../../components/WysiwygField"
import { useTecnicaCreate, useTecnicaLazy, useTecnicheLazy } from "../../hooks/tecniche"
import TecnicaModal from "../Tecniche/TecnicaModal"

export default function ImmagineForm({ col = 8, showEdizione = false }: { col?: number; showEdizione?: boolean }) {
  const { setFieldValue, values, errors, submitCount } = useFormikContext<ImmagineFormType>()

  const fetchEdizione = useEdizioneLazy()
  const fetchEdizioni = useEdizioniLazy()

  const fetchTipologieImmagini = useTipologieImmaginiLazy()
  const fetchTipologiaImmagine = useTipologiaImmagineLazy()

  const [modalStateCreateEdizione, modalActionsCreateEdizione] = useModalTrigger()
  const [modalStateCreateTag, modalActionsCreateTag] = useModalTrigger()
  const [modalStateCreateTipologiaImmagine, modalActionsCreateTipologiaImmagine] = useModalTrigger()
  const [modalStateCreatePersona, modalActionsCreatePersona] = useModalTrigger()
  const [modalStateCreateTecnica, modalActionsCreateTecnica] = useModalTrigger()

  const createEdizione = useEdizioneCreate()
  const createTag = useTagCreate()
  const createTipologiaImmagine = useTipologiaImmagineCreate()
  const createPersona = usePersonaCreate()
  const createTecnica = useTecnicaCreate()

  const fetchTag = useTagLazy()
  const fetchTags = useTagsLazy()

  const fetchPersone = usePersoneLazy()
  const fetchPersona = usePersonaLazy()

  const fetchTecnica = useTecnicaLazy()
  const fetchTecniche = useTecnicheLazy()

  return (
    <Col xs={col} className="d-flex flex-column align-items-stretch gap-4">
      <CharField
        label="Titolo"
        layout="horizontal"
        value={values.titolo}
        onChange={(value) => {
          setFieldValue("titolo", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "titolo") : undefined}
        errorMessage={getIn(errors, "titolo")}
        required
      />
      {showEdizione && (
        <AsyncSingleSelectField
          label="Edizione"
          placeholder="Seleziona un'edizione"
          layout="horizontal"
          onChange={(value) => {
            setFieldValue("edizione", value)
          }}
          clearable
          value={values.edizione}
          loadOptions={(inputValue: string) => {
            return fetchEdizioni({ search: inputValue }).then((edizioni) => {
              return edizioni.results?.map((edizione) => ({
                label: edizione.opera_data?.titolo + " - " + edizione.titolo_completo,
                value: edizione.id,
              }))
            })
          }}
          loadInitialValue={(id: number) =>
            fetchEdizione(id).then((edizione) => {
              return { label: edizione.opera_data?.titolo + " - " + edizione.titolo_completo, value: edizione.id }
            })
          }
          required
          isValid={submitCount > 0 ? !getIn(errors, "edizione") : undefined}
          errorMessage={getIn(errors, "edizione")}
        >
          <SingleSelectField.Add
            onClick={() => {
              modalActionsCreateEdizione.open()
            }}
          />
        </AsyncSingleSelectField>
      )}
      <AsyncSingleSelectField
        label="Incisore"
        layout="horizontal"
        placeholder="Seleziona un incisore"
        onChange={(value) => {
          setFieldValue("incisore", value)
        }}
        clearable
        value={values.incisore}
        loadOptions={(inputValue: string) => {
          return fetchPersone({ search: inputValue }).then((persone) => {
            return persone.results.map((persona) => ({
              label: persona.nome,
              value: persona.id,
            }))
          })
        }}
        loadInitialValue={(id: number) =>
          fetchPersona(id).then((persona) => {
            return { label: persona.nome, value: persona.id }
          })
        }
      >
        <SingleSelectField.Add
          onClick={() => {
            modalActionsCreatePersona.open()
          }}
        />
      </AsyncSingleSelectField>
      <AsyncSingleSelectField
        label="Tipologia"
        layout="horizontal"
        placeholder="Seleziona una tipologia"
        onChange={(value) => {
          setFieldValue("tipologia", value)
        }}
        clearable
        value={values.tipologia}
        loadOptions={(inputValue: string) => {
          return fetchTipologieImmagini({ search: inputValue }).then((tipologie) => {
            return tipologie.results.map((tipologia) => ({
              label: tipologia.nome,
              value: tipologia.id,
            }))
          })
        }}
        loadInitialValue={(id: number) =>
          fetchTipologiaImmagine(id).then((tipologia) => {
            return { label: tipologia.nome, value: tipologia.id }
          })
        }
        required
        isValid={submitCount > 0 ? !getIn(errors, "tipologia") : undefined}
        errorMessage={getIn(errors, "tipologia")}
      >
        <SingleSelectField.Add
          onClick={() => {
            modalActionsCreateTipologiaImmagine.open()
          }}
        />
      </AsyncSingleSelectField>

      <ImageField
        label="Immagine"
        layout="horizontal"
        value={values.immagine}
        onChange={(value) => {
          setFieldValue("immagine", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "immagine") : undefined}
        errorMessage={getIn(errors, "immagine")}
        required
      />
      <WysiwygField
        label="Descrizione"
        layout="horizontal"
        value={values.descrizione}
        onChange={(value) => {
          setFieldValue("descrizione", value)
        }}
      />
      <AsyncSingleSelectField
        label="Tecnica"
        layout="horizontal"
        placeholder="Seleziona una tecnica"
        onChange={(value) => {
          setFieldValue("tecnica", value)
        }}
        clearable
        value={values.tecnica}
        loadOptions={(inputValue: string) => {
          return fetchTecniche({ search: inputValue }).then((tecniche) => {
            return tecniche.results.map((tecnica) => ({
              label: tecnica.nome,
              value: tecnica.id,
            }))
          })
        }}
        loadInitialValue={(id: number) =>
          fetchTecnica(id).then((tecnica) => {
            return { label: tecnica.nome, value: tecnica.id }
          })
        }
      >
        <SingleSelectField.Add
          onClick={() => {
            modalActionsCreateTecnica.open()
          }}
        />
      </AsyncSingleSelectField>
      <CharField
        label="Ubicazione"
        layout="horizontal"
        value={values.ubicazione}
        onChange={(value) => {
          setFieldValue("ubicazione", value)
        }}
      />
      <AsyncMultiSelectField
        label="Tags"
        placeholder="Seleziona uno o più tags"
        layout="horizontal"
        onChange={(value) => {
          setFieldValue("tags", value)
        }}
        clearable
        value={values.tags}
        loadInitialValues={(ids: number[]) =>
          Promise.all(ids.map((id) => fetchTag(id).then((tag) => ({ label: tag.nome, value: tag.id }))))
        }
        loadOptions={(inputValue: string) => {
          return fetchTags({ search: inputValue }).then((tags) => {
            return tags.results.map((tag) => ({
              label: tag.nome,
              value: tag.id,
            }))
          })
        }}
      >
        <SingleSelectField.Add
          onClick={() => {
            modalActionsCreateTag.open()
          }}
        />
      </AsyncMultiSelectField>
      <NumberField
        label="Numero occorrenze"
        layout="horizontal"
        value={values.numero_occorrenze}
        onChange={(value) => {
          setFieldValue("numero_occorrenze", value)
        }}
        min={1}
      />
      <SwitchField
        label="Mostra nella pagina CIPEC introduzione"
        layout="horizontal"
        value={values.mostra_in_cipec}
        onChange={(checked) => {
          setFieldValue("mostra_in_cipec", checked)
        }}
      />
      {modalStateCreateEdizione.isOpen && (
        <EdizioneModal
          show={modalStateCreateEdizione.isOpen}
          onClose={modalActionsCreateEdizione.close}
          showOpera={showEdizione}
          save={(values) => {
            return createEdizione.mutateAsync(values).then((edizione) => {
              setFieldValue("edizione", edizione.id)
              modalActionsCreateEdizione.close()
            })
          }}
          title="Crea edizione"
        />
      )}
      {modalStateCreateTag.isOpen && (
        <TagModal
          show={modalStateCreateTag.isOpen}
          onClose={modalActionsCreateTag.close}
          save={(valuesSave, actions) => {
            return createTag
              .mutateAsync(valuesSave)
              .then((tag) => {
                setFieldValue("tags", [...values.tags, tag.id])
                modalActionsCreateTag.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Crea tag"
        />
      )}
      {modalStateCreateTecnica.isOpen && (
        <TecnicaModal
          show={modalStateCreateTecnica.isOpen}
          onClose={modalActionsCreateTecnica.close}
          save={(valuesSave, actions) => {
            return createTecnica
              .mutateAsync(valuesSave)
              .then((tecnica) => {
                setFieldValue("tecnica", tecnica.id)
                modalActionsCreateTecnica.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Crea tecnica"
        />
      )}
      {modalStateCreateTipologiaImmagine.isOpen && (
        <TipologiaImmagineModal
          show={modalStateCreateTipologiaImmagine.isOpen}
          onClose={modalActionsCreateTipologiaImmagine.close}
          save={(valuesSave, actions) => {
            return createTipologiaImmagine
              .mutateAsync(valuesSave)
              .then((tipologia) => {
                setFieldValue("tipologia", tipologia.id)
                modalActionsCreateTipologiaImmagine.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Crea tipologia immagine"
        />
      )}
      {modalStateCreatePersona.isOpen && (
        <EdizioneModal
          show={modalStateCreatePersona.isOpen}
          onClose={modalActionsCreatePersona.close}
          save={(values) => {
            return createPersona.mutateAsync(values).then((persona) => {
              setFieldValue("incisore", persona.id)
              modalActionsCreatePersona.close()
            })
          }}
          title="Crea incisore"
        />
      )}
    </Col>
  )
}
